import {TypeLocalNameEnum} from 'domain/usecases';
import {QueryParamsForTaggingEnum} from 'presentation/constants';
import {useWellBornEligibility} from 'presentation/modules/booking/shared/hooks/useWellBornEligibility';
import {getWellBornEligibility} from 'presentation/modules/booking/shared/services/getWellBornEligibility';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useHideSearchBarStore} from 'presentation/modules/booking/shared/store/useHideSearchBarStore';
import {useHideStepBarStore} from 'presentation/modules/booking/shared/store/useHideStepBarStore';
import {useQueryParams} from 'presentation/pages';
import {Specialty} from 'presentation/utils';
import {useEffect, useState} from 'react';
import {UseBeBornWellNotRegisterProps} from './types';
import {CheckBookingModalEnum} from 'presentation/modules/booking/health/ui/pages/HealthBooking/steps/HealthSpecialtySelection/components/HealthSpecialtiesList/hooks/useHealthSpecialtiesSelection/types';
import {translations} from 'presentation/translations-new';

const {verifyingAuth} = translations['pt-br'].privateFlow.bookingFlow.specialtyFlow;
const {loading} = translations['pt-br'].commons;

export function useBeBornWellNotRegister({
	selectedSpecialty,
	handleSelectCard,
	checkBookingModal,
}: UseBeBornWellNotRegisterProps) {
	const setHideSearchBarState = useHideSearchBarStore(state => state.setHideSearchBar);
	const setHideStepBarState = useHideStepBarStore(state => state.setHideStepBar);
	const {setSearchParams} = useQueryParams();

	const [selectedSpecialtyState, setSelectedSpecialtyState] = useState<Specialty | null>(
		selectedSpecialty || null,
	);
	const [showBeBornWellCard, setShowBeBornWellCard] = useState(false);
	const [openBeBornWellDrawer, setOpenBeBornWellDrawer] = useState(false);
	const [isProcessing, setIsProcessing] = useState(false);
	const bookingData = useBookingStore(state => state.bookingData);
	const {ufCode, cityName, typeLocalName, isManualLocation} = bookingData;
	const {setEligibilityData} = useWellBornEligibility();

	const isUsingGeolocation = !isManualLocation;

	const handleRegisterClick = async (specialty: Specialty) => {
		setSearchParams(prev => {
			prev.delete(QueryParamsForTaggingEnum.SHOW_BE_BORN_WELL_NOT_REGISTER);
			return prev;
		});
		setIsProcessing(true);
		setSelectedSpecialtyState(specialty);
		try {
			const eligibilityResponse = await getWellBornEligibility({
				stateCode: ufCode,
				typeLocalName: isUsingGeolocation
					? TypeLocalNameEnum.CIDADE
					: (typeLocalName ?? TypeLocalNameEnum.CIDADE),
				localName: cityName,
				specialtyCode: specialty.code.toString(),
			});

			if (!eligibilityResponse) {
				await handleSelectCard(specialty);
				setIsProcessing(false);
				return;
			}

			setEligibilityData(eligibilityResponse);
			setOpenBeBornWellDrawer(true);
		} catch (error) {
			await handleSelectCard(specialty);
		} finally {
			setIsProcessing(false);
		}
	};

	const handleNotPregnantClick = () => {
		setOpenBeBornWellDrawer(false);
		if (selectedSpecialtyState) {
			handleSelectCard(selectedSpecialtyState);
		}
	};

	const handleShowBeBornWellCard = () => {
		setOpenBeBornWellDrawer(false);
		setShowBeBornWellCard(true);
	};

	const handleContinueToBooking = () => {
		if (selectedSpecialtyState) {
			handleSelectCard(selectedSpecialtyState);
		}
	};

	const loadingText =
		checkBookingModal === CheckBookingModalEnum.VERIFYING_AUTH ? verifyingAuth : loading;

	useEffect(() => {
		setHideSearchBarState(true);
		setHideStepBarState(true);
	}, []);

	return {
		showBeBornWellCard,
		openBeBornWellDrawer,
		isProcessing,
		handleRegisterClick,
		handleNotPregnantClick,
		handleShowBeBornWellCard,
		handleContinueToBooking,
		loadingText,
		setOpenBeBornWellDrawer,
	};
}
